import { useState } from "react";
import { CONTACT_LABELS, ContactIcon } from "@/components/ContactsField";
import { displaySocialValue } from "@/lib/social";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";

const LABELS = CONTACT_LABELS;

// Self-hosted brand marks (downloaded into /public) so the buttons never depend
// on an external favicon/CDN at runtime.
const BRAND_LOGO = { maxpreps: "/maxpreps.png", hudl: "/hudl.png" };
const BRAND_LABEL = { maxpreps: "MaxPreps", hudl: "Hudl" };

function BrandLink({ type, href, testid, cardCls, fallbackInner, accent }) {
    const [failed, setFailed] = useState(false);
    if (failed) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cardCls} style={{ "--acc": accent }}>
                {fallbackInner}
            </a>
        );
    }
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} title={BRAND_LABEL[type]} className={cardCls} style={{ "--acc": accent }}>
            <span className="w-7 h-7 rounded bg-white flex items-center justify-center shrink-0 overflow-hidden">
                <img src={BRAND_LOGO[type]} alt={BRAND_LABEL[type]} onError={() => setFailed(true)} className="w-full h-full object-contain p-0.5" />
            </span>
            <span className="text-sm text-white group-hover:[color:var(--acc)] transition-colors">{BRAND_LABEL[type]}</span>
        </a>
    );
}

function hrefFor(type, value) {
    const v = (value || "").trim();
    const handle = v.replace(/^@/, "");
    const url = (u) => (u.startsWith("http") ? u : `https://${u}`);
    switch (type) {
        case "phone": return `tel:${v.replace(/[^0-9+]/g, "")}`;
        case "email": return `mailto:${v}`;
        case "instagram": return v.startsWith("http") ? v : `https://instagram.com/${handle}`;
        case "tiktok": return v.startsWith("http") ? v : `https://tiktok.com/@${handle}`;
        case "youtube": return v.startsWith("http") ? v : `https://youtube.com/${v.startsWith("@") ? v : handle}`;
        case "x": return v.startsWith("http") ? v : `https://x.com/${handle}`;
        case "facebook": return v.startsWith("http") ? v : `https://facebook.com/${handle}`;
        default: return url(v);
    }
}

// Content-hugging, wrapping list of contact/social links. Cards size to their
// content and pack as many per row as fit. Renders nothing when empty.
// `inline` drops the wrapper/title so cards flow as siblings in a parent flex row.
export default function ContactsDisplay({ contacts, accent = "#22c55e", testidPrefix = "contact", hideTitle = false, inline = false, title = "Contact / Social Media" }) {
    const items = (contacts || []).filter((c) => c && (c.value || "").trim());
    if (!items.length) return null;
    const isLink = (t) => !["phone", "email"].includes(t);
    const legacyCopy = (value) => {
        const ta = document.createElement("textarea");
        ta.value = value; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        let ok = false;
        try { ok = document.execCommand("copy"); } catch { ok = false; }
        document.body.removeChild(ta);
        return ok;
    };
    const copyEmail = async (value) => {
        let copied = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(value);
                copied = true;
            }
        } catch {
            copied = false;
        }
        if (!copied) copied = legacyCopy(value);
        if (copied) toast.success("Email address copied", { description: value });
        else toast.error("Couldn't copy — long-press to copy manually");
    };
    const cardCls = "inline-flex items-center gap-2.5 px-3 py-2 bg-[#0f0f0f] border border-[#2a2a2a] hover:bg-[#181818] transition-colors group cursor-pointer text-left";
    const inner = (c) => (
        <>
            <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: `${accent}1f`, color: accent }}>
                <ContactIcon type={c.type} className="h-3.5 w-3.5" />
            </span>
            <span className="min-w-0">
                <span className="block text-[9px] uppercase tracking-[0.14em] text-[#666] leading-none">{LABELS[c.type] || "Link"}</span>
                <span className="block text-sm text-white truncate max-w-[15rem] group-hover:[color:var(--acc)] transition-colors">{displaySocialValue(c.type, c.value)}</span>
            </span>
            {c.verified && (
                <span title="Verified owner contact" data-testid="contact-verified-check" className="shrink-0 text-[#22c55e] inline-flex items-center">
                    <BadgeCheck className="h-4 w-4" />
                </span>
            )}
        </>
    );
    const renderCard = (c, i) => {
        const key = `${c.type}-${i}`;
        const testid = `${testidPrefix}-${i}`;
        if (c.type === "maxpreps" || c.type === "hudl") {
            return <BrandLink key={key} type={c.type} href={hrefFor(c.type, c.value)} testid={testid} cardCls={cardCls} fallbackInner={inner(c)} accent={accent} />;
        }
        if (c.type === "email") {
            return (
                <button key={key} type="button" onClick={() => copyEmail(c.value)} title="Copy email address" data-testid={testid} className={cardCls} style={{ "--acc": accent }}>
                    {inner(c)}
                </button>
            );
        }
        return (
            <a key={key} href={hrefFor(c.type, c.value)} target={isLink(c.type) ? "_blank" : undefined} rel={isLink(c.type) ? "noopener noreferrer" : undefined} data-testid={testid} className={cardCls} style={{ "--acc": accent }}>
                {inner(c)}
            </a>
        );
    };
    const cards = items.map(renderCard);
    if (inline) return <>{cards}</>;
    return (
        <div data-testid={`${testidPrefix}s-display`}>
            {!hideTitle && <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">{title}</div>}
            <div className="flex flex-wrap gap-2">{cards}</div>
        </div>
    );
}
